// frontend/src/pages/AttendancePage.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { DatePicker, Table, Radio, Button, Spin, Typography, Select, Space, List, Card, Divider, Row, Col, App as AntApp, Segmented, Avatar, Tag, Alert } from 'antd';
import { useAuth } from '../context/AuthContext';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getAllStudents } from '../services/studentService';
import { getAllSubjects } from '../services/subjectService';
import { getClassAssignments } from '../services/assignmentService';
import { getAttendanceByDateAndSubject, saveAttendance } from '../services/attendanceService';
import { getAllClasses } from '../services/classService';
import api from '../services/api';
import useResponsive from '../hooks/useResponsive';
import { SaveOutlined, WhatsAppOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import maleAvatar from '../assets/male.png';
import femaleAvatar from '../assets/female.png';

const { Title, Text } = Typography;
const { Option } = Select;

const AttendancePage = () => {
    const { message: messageApi } = AntApp.useApp(); // Use AntApp.useApp for messaging
    const { user } = useAuth();
    const { currentYear } = useAcademicYear();
    const { isMobile } = useResponsive();
    const [allClasses, setAllClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [allAssignments, setAllAssignments] = useState([]);
    const [attendanceData, setAttendanceData] = useState({});
    const [studentsForClass, setStudentsForClass] = useState([]);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in AttendancePage:", err);
            }
        };
        fetchSettings();
    }, []);

    const isEntryDisabled = user.role === 'teacher' && settings?.allow_teacher_attendance_entry === false;

    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedClassId, setSelectedClassId] = useState(undefined);
    const [selectedSubjectId, setSelectedSubjectId] = useState(undefined);
    const [statusFilter, setStatusFilter] = useState('all');

    const [loading, setLoading] = useState({ masters: true, attendance: false });
    const [saving, setSaving] = useState(false);

    const selectedClass = useMemo(() => allClasses.find(c => c.id === selectedClassId), [allClasses, selectedClassId]);
    const attendanceMode = 'daily'; // Fixed to daily mode only

    const calculateAbsentees = (students, data, mode) => {
        let absenteeCount = 0;

        students.forEach(student => {
            const status = data[student.id];

            if (mode === 'fnan') {
                const fn = status?.fn || 'present';
                const an = status?.an || 'present';
                // Count if student was absent for at least one half of the day
                if (fn === 'absent' || an === 'absent') {
                    absenteeCount++;
                }
            } else if (status === 'absent') {
                absenteeCount++;
            }
        });

        return absenteeCount;
    };

    const handleShareAttendance = async () => {
        await handleSave();
        if (!selectedDate || !selectedClassId) {
            messageApi.warning('Please select a Date and a Class first.');
            return;
        }

        const dateStr = selectedDate.format('DD MMMM YYYY');
        const className = allClasses.find(c => c.id === selectedClassId)?.name || 'the selected class';
        const subjectName = selectedSubjectId ? ` for ${subjects.find(s => s.id === selectedSubjectId)?.name}` : '';
        const modeLabel = attendanceMode === 'period' ? 'Period-wise' : (attendanceMode === 'fnan' ? 'FN/AN' : 'Daily');

        // Calculate the number of recorded absentees based on current data
        const absenteeCount = calculateAbsentees(filteredStudents, attendanceData, attendanceMode);

        // Build absentee names list (with FN/AN details for fnan mode)
        const absenteesDetail = filteredStudents.reduce((acc, student) => {
            const status = attendanceData[student.id];
            if (attendanceMode === 'fnan') {
                const fn = status?.fn || 'present';
                const an = status?.an || 'present';
                const parts = [];
                if (fn === 'absent') parts.push('FN');
                if (an === 'absent') parts.push('AN');
                if (parts.length) acc.push(`${student.student_name} (${parts.join(', ')})`);
            } else {
                if (status === 'absent') acc.push(student.student_name);
            }
            return acc;
        }, []);

        let messageText = `*Attendance Report:*\n`;
        messageText += `Date: ${dateStr}\n`;
        messageText += `Class: ${className}\n`;
        // messageText += `Mode: ${modeLabel}${subjectName}.\n\n`;

        if (absenteeCount > 0) {
            messageText += `*Total Absentees: ${absenteeCount}*\n\n`;
            // Limit number of names shown to avoid huge messages
            const MAX_LIST = 60;
            if (absenteesDetail.length > 0) {
                if (absenteesDetail.length > MAX_LIST) {
                    const shown = absenteesDetail.slice(0, MAX_LIST).map((name, index) => `${index + 1}. ${name}`).join('\n');
                    const remaining = absenteesDetail.length - MAX_LIST;
                    messageText += `Absentees:\n${shown}\nand ${remaining} more...\n`;
                } else {
                    const numberedAbsentees = absenteesDetail.map((name, index) => `${index + 1}. ${name}`).join('\n');
                    messageText += `Absentees:\n${numberedAbsentees}\n`;
                }
            }
            messageText += `\nTotal Students: ${filteredStudents.length}\n\n`;
            // messageText += `Please verify the records in the School Management System.`;
        } else if (filteredStudents.length > 0) {
            messageText += `*All ${filteredStudents.length} students currently marked present.*\n\n`;
            // messageText += `Please finalize the attendance records.`;
        } else {
            messageText += `No students enrolled or attendance records found yet.`;
        }

        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(messageText)}`;
        window.open(whatsappUrl, '_blank');
    };



    const fetchMasters = useCallback(async () => {
        setLoading(prev => ({ ...prev, masters: true }));
        try {
            const [studentsData, subjectsData, classesData] = await Promise.all([getAllStudents(), getAllSubjects(), getAllClasses()]);
            // Ensure students state stores ALL students with necessary key props
            setStudents(studentsData.map(s => ({
                ...s,
                key: s.id,
                class_id: s.class_id,
                roll_no: s.roll_no,
                // Make sure to include the current class ID for filtering
            })));
            // console.log('Fetched Students:', studentsData.map(s => s.student_name));
            setSubjects(subjectsData);
            setAllClasses(classesData);
        } catch (error) {
            messageApi.error('Failed to load initial data.');
        } finally {
            setLoading(prev => ({ ...prev, masters: false }));
        }
    }, [messageApi]);

    useEffect(() => {
        if (currentYear) {
            fetchMasters();
        }
    }, [currentYear, fetchMasters]);

    const availableClassesForUser = useMemo(() => {
        if (!user) {
            messageApi.error('User not authenticated.');
            return [];
        };
        if (user.role === 'principal' || user.role === 'super_admin') {
            return allClasses.sort((a, b) => a.name.localeCompare(b.name));
        }
        if (user.role === 'teacher') {
            if (settings?.allow_teacher_attendance_entry !== false) {
                return allClasses.sort((a, b) => a.name.localeCompare(b.name));
            }
            const classTeacherClass = allClasses.find(c => c.class_teacher_id === user.id);

            const subjectTeacherClassIds = [...new Set(allAssignments.filter(a => a.teacher_id === user.id).map(a => a.class_id))];
            const allVisibleClassIds = new Set(subjectTeacherClassIds);
            if (classTeacherClass) {
                allVisibleClassIds.add(classTeacherClass.id);
            }
            return allClasses.filter(c => allVisibleClassIds.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
        }
        return [];
    }, [user, allClasses, allAssignments, settings]);

    useEffect(() => {
        if ((user.role === 'teacher' || user.role === 'principal') && allClasses.length > 0) {
            const fetchAssignments = async () => {
                try {
                    // For a teacher, fetch assignments from ALL classes in parallel to determine which ones they teach.
                    const assignmentPromises = allClasses
                        .filter(c => c.id)
                        .map(c =>
                            getClassAssignments(c.id).then(assignments =>
                                assignments.map(a => ({ ...a, class_id: c.id }))
                            )
                        );

                    const assignmentsByClass = await Promise.all(assignmentPromises);
                    setAllAssignments(assignmentsByClass.flat());
                } catch (error) {
                    messageApi.error("Failed to fetch class assignments.");
                    setAllAssignments([]); // Reset on error to avoid inconsistent state
                }
            };
            fetchAssignments();
        }
    }, [user, allClasses, messageApi]);

    useEffect(() => {
        const fetchAttendance = async () => {
            if (!selectedDate || !selectedClassId) {
                setAttendanceData({});
                setStudentsForClass([]);
                return;
            }
            setLoading(prev => ({ ...prev, attendance: true }));
            try {
                const dateStr = selectedDate.format('YYYY-MM-DD');
                const records = await getAttendanceByDateAndSubject(dateStr, null, selectedClassId, 'daily');

                // Deduplicate students by ID
                const uniqueStudentsMap = new Map();
                records.forEach(rec => {
                    if (!uniqueStudentsMap.has(rec.student_id)) {
                        uniqueStudentsMap.set(rec.student_id, {
                            id: rec.student_id,
                            key: rec.student_id,
                            student_name: rec.student_name,
                            admission_no: rec.admission_no,
                            roll_no: rec.roll_no,
                            class_id: rec.class_id,
                            class_name: rec.class_name,
                            gender: rec.gender // Ensure gender is captured if available
                        });
                    }
                });
                setStudentsForClass(Array.from(uniqueStudentsMap.values()));

                // Build attendance data map for daily mode
                const recordsMap = {};
                records.forEach(rec => {
                    recordsMap[rec.student_id] = rec.status;
                });

                setAttendanceData(recordsMap);
            } catch (error) { messageApi.error('Failed to fetch attendance records.'); }
            finally { setLoading(prev => ({ ...prev, attendance: false })); }
        };
        fetchAttendance();
    }, [selectedDate, selectedClassId, messageApi]);

    const handleStatusChange = (studentId, status, partOfDay = null) => {
        setAttendanceData(prevData => {
            if (partOfDay) {
                return { ...prevData, [studentId]: { ...prevData[studentId], [partOfDay]: status } };
            }
            return { ...prevData, [studentId]: status };
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const records = filteredStudents.map(student => ({
                student_id: student.id,
                status: attendanceData[student.id] || 'present'
            }));
            await saveAttendance(dateStr, null, selectedClassId, records, 'daily');
            messageApi.success('Attendance saved successfully!');
        } catch (error) { messageApi.error(error.response?.data?.error || 'Failed to save attendance.'); }
        finally { setSaving(false); }
    };

    const handleClassChange = (classId) => {
        setSelectedClassId(classId);
        setSelectedSubjectId(undefined);
        setStatusFilter('all');
        setStudentsForClass([]); // Clear students immediately when class changes
    };

    const availableSubjects = useMemo(() => {
        if (!selectedClassId) return [];
        if (user.role === 'principal' || user.role === 'super_admin' || settings?.allow_teacher_attendance_entry !== false || (selectedClass && selectedClass.class_teacher_id === user.id)) return subjects;
        const subjectIds = allAssignments.filter(a => a.teacher_id === user.id && a.class_id === selectedClassId).map(a => a.subject_id);
        return subjects.filter(s => subjectIds.includes(s.id));
    }, [selectedClassId, allAssignments, subjects, user, selectedClass, settings]);

    const getStudentEffectiveStatus = useCallback((studentId) => {
        const studentStatus = attendanceData[studentId];
        return studentStatus || 'present';
    }, [attendanceData]);

    const filteredStudents = useMemo(() => {
        if (!selectedClassId) return [];

        let classStudents = [...studentsForClass];

        if (statusFilter !== 'all') {
            classStudents = classStudents.filter(student => {
                const status = getStudentEffectiveStatus(student.id);
                return status === statusFilter;
            });
        }

        return classStudents.sort((a, b) => {
            const rollA = a.roll_no || 9999;
            const rollB = b.roll_no || 9999;
            if (rollA !== rollB) return rollA - rollB;
            return a.student_name.localeCompare(b.student_name);
        });
    }, [studentsForClass, selectedClassId, statusFilter, getStudentEffectiveStatus]);

    const getRowStyle = (studentId) => {
        const status = getStudentEffectiveStatus(studentId);
        if (status === 'absent') return { backgroundColor: '#fff1f0' };
        if (status === 'late') return { backgroundColor: '#fffbe6' };
        return {};
    };

    const columns = [
        { title: 'Roll No', dataIndex: 'roll_no', width: 70 },
        { title: 'Admission No', dataIndex: 'admission_no', width: 120 },
        { title: 'Student Name', dataIndex: 'student_name' },
        {
            title: 'Status',
            render: (_, record) => (
                <Radio.Group 
                    size="small" 
                    value={attendanceData[record.id] || 'present'} 
                    onChange={e => handleStatusChange(record.id, e.target.value)}
                    disabled={isEntryDisabled}
                >
                    <Radio.Button value="present">Present</Radio.Button>
                    <Radio.Button value="absent">Absent</Radio.Button>
                    <Radio.Button value="late">Late</Radio.Button>
                </Radio.Group>
            ),
        }
    ];

    const getAvatarSrc = (item) => {
        if (item.profile_picture_url) {
            return `${process.env.REACT_APP_API_URL || ''}${item.profile_picture_url}`;
        }
        return item.gender === 'Female' ? femaleAvatar : maleAvatar;
    };

    const renderMobileList = () => (
        <List
            loading={loading.attendance}
            dataSource={filteredStudents}
            grid={{ gutter: 16, column: 1 }}
            renderItem={item => {
                const status = attendanceData[item.id] || 'present';
                return (
                    <List.Item style={{ padding: 0, marginBottom: 12 }}>
                        <Card
                            bodyStyle={{ padding: 12 }}
                            style={{
                                borderRadius: 12,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                border: status === 'absent' ? '1px solid #ffccc7' : (status === 'late' ? '1px solid #ffe58f' : '1px solid #f0f0f0'),
                                background: status === 'absent' ? '#fff2f0' : (status === 'late' ? '#fffbe6' : '#fff'),
                                transition: 'all 0.3s ease'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                                <Avatar
                                    size={48}
                                    src={getAvatarSrc(item)}
                                    style={{ border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                                />
                                <div style={{ marginLeft: 12, flex: 1 }}>
                                    <Text strong style={{ fontSize: 16, display: 'block', color: '#1f1f1f' }}>
                                        {item.student_name}
                                    </Text>
                                    <Space size={4}>
                                        <Tag color="blue" style={{ margin: 0 }}>Roll: {item.roll_no}</Tag>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Adm: {item.admission_no}</Text>
                                    </Space>
                                </div>
                            </div>

                             <Segmented
                                block
                                value={status}
                                onChange={(val) => handleStatusChange(item.id, val)}
                                disabled={isEntryDisabled}
                                options={[
                                    {
                                        label: (
                                            <div style={{ padding: 4 }}>
                                                <CheckCircleOutlined style={{ color: status === 'present' ? '#389e0d' : undefined }} />
                                                <div style={{ fontSize: 11 }}>Present</div>
                                            </div>
                                        ),
                                        value: 'present',
                                    },
                                    {
                                        label: (
                                            <div style={{ padding: 4 }}>
                                                <CloseCircleOutlined style={{ color: status === 'absent' ? '#cf1322' : undefined }} />
                                                <div style={{ fontSize: 11 }}>Absent</div>
                                            </div>
                                        ),
                                        value: 'absent',
                                    },
                                    {
                                        label: (
                                            <div style={{ padding: 4 }}>
                                                <ClockCircleOutlined style={{ color: status === 'late' ? '#d48806' : undefined }} />
                                                <div style={{ fontSize: 11 }}>Late</div>
                                            </div>
                                        ),
                                        value: 'late',
                                    },
                                ]}
                                style={{
                                    background: 'rgba(0,0,0,0.02)',
                                    padding: 4,
                                    borderRadius: 8
                                }}
                            />
                        </Card>
                    </List.Item>
                );
            }}
        />
    );

    return (
        <div style={{ paddingBottom: isMobile && selectedClassId ? 140 : undefined }}>
            <Title level={2} style={{ marginBottom: 24, fontSize: isMobile ? 24 : 30 }}>Mark Attendance</Title>

            {isEntryDisabled && (
                <Alert
                    message="Attendance Entry Disabled"
                    description="You do not have permission to mark attendance. This feature has been disabled by the administrator."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 24, borderRadius: 12 }}
                />
            )}

            <Card style={{ marginBottom: 24, borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }} bodyStyle={{ padding: isMobile ? 16 : 24 }}>
                <Spin spinning={loading.masters}>
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Text strong style={{ marginBottom: 6, display: 'block' }}>Select Date</Text>
                                <DatePicker
                                    value={selectedDate}
                                    onChange={setSelectedDate}
                                    format="DD MMMM YYYY"
                                    allowClear={false}
                                    style={{ width: '100%', height: 40, borderRadius: 8 }}
                                    disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                                />
                            </Col>
                            <Col xs={24} md={12}>
                                <Text strong style={{ marginBottom: 6, display: 'block' }}>Select Class</Text>
                                <Select
                                    style={{ width: '100%', height: 40 }}
                                    placeholder="Select a Class"
                                    onChange={handleClassChange}
                                    value={selectedClassId}
                                    allowClear
                                    options={availableClassesForUser.map(c => ({ label: c.name, value: c.id }))}
                                />
                            </Col>
                        </Row>

                        {selectedClassId && (
                            <>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                    <Segmented
                                        value={statusFilter}
                                        onChange={setStatusFilter}
                                        options={[
                                            { label: 'All Students', value: 'all' },
                                            { label: 'Present', value: 'present' },
                                            { label: 'Absent', value: 'absent' },
                                        ]}
                                        style={{ padding: 4, background: '#f5f5f5' }}
                                    />
                                </div>

                                {!isMobile && (
                                    <Row justify="end" gutter={12} style={{ marginTop: 16 }}>
                                        <Col>
                                            <Button
                                                icon={<WhatsAppOutlined />}
                                                onClick={handleShareAttendance}
                                                style={{ backgroundColor: '#25D366', color: 'white', borderColor: '#25D366', borderRadius: 8 }}
                                                disabled={!selectedDate || !selectedClassId}
                                                size="large"
                                            >
                                                Share Status
                                            </Button>
                                        </Col>
                                        <Col>
                                            <Button
                                                type="primary"
                                                icon={<SaveOutlined />}
                                                onClick={handleSave}
                                                loading={saving}
                                                disabled={isEntryDisabled || !selectedDate || !selectedClassId || selectedDate.isBefore(dayjs().subtract(7, 'days'))}
                                                size="large"
                                                style={{ borderRadius: 8, fontWeight: 600 }}
                                            >
                                                Confirm Attendance
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                    </Space>
                </Spin>
            </Card>

            <Card style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.03)', background: isMobile ? 'transparent' : '#fff' }} bodyStyle={{ padding: isMobile ? 0 : 24 }} bordered={!isMobile}>
                {isMobile ? renderMobileList() : (
                    <Table
                        dataSource={filteredStudents}
                        columns={columns}
                        bordered
                        pagination={false}
                        loading={loading.attendance}
                        onRow={(record) => ({ style: getRowStyle(record.id) })}
                        scroll={{ x: 'max-content' }}
                    />
                )}
            </Card>

            {/* MOBILE FIXED BOTTOM ACTIONS */}
            {isMobile && selectedClassId && (
                <div style={{
                    position: 'fixed',
                    zIndex: 1100,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    padding: '12px 16px 24px',
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    borderTop: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
                }}>
                    <Row gutter={12}>
                        <Col span={10}>
                            <Button
                                icon={<WhatsAppOutlined />}
                                onClick={handleShareAttendance}
                                block
                                size="large"
                                style={{
                                    backgroundColor: '#25D366',
                                    color: 'white',
                                    borderColor: '#25D366',
                                    borderRadius: 12,
                                    height: '52px',
                                    fontSize: '16px',
                                    fontWeight: 600
                                }}
                                disabled={!selectedDate || !selectedClassId}
                            >
                                Share
                            </Button>
                        </Col>
                        <Col span={14}>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={saving}
                                disabled={isEntryDisabled || !selectedDate || !selectedClassId}
                                block
                                size="large"
                                style={{
                                    borderRadius: 12,
                                    height: '52px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
                                }}
                            >
                                Save Status
                            </Button>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default AttendancePage;